import React, { useEffect, useState } from 'react';
import { Badge, Box, CssBaseline, IconButton, Stack, Toolbar } from '@mui/material';
import { handleErrors } from '../../utils/common';
import { logout, selectCurrentUser } from '../../features/auth/authSlice';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useAppDispatch } from '../../hooks/reduxHooks';
import { useAppSelector } from '../../hooks/reduxHooks';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useLogoutMutation } from '../../features/auth/authApi';
import { useNavigate } from 'react-router-dom';
import { useRoles } from '../../hooks/useRoles';
import { useTranslation } from 'react-i18next';
import AdminLayout from './RoutesManager/AdminLayout';
import AppBar from './AppBar';
import SideMenu from '../SideMenu';
import logoThemeBlue from '../../assets/image/logo-blue.png';
import logoThemeGreen from '../../assets/image/logo-green.png';
import MenuIcon from '@mui/icons-material/Menu';
import ScrollToTop from '../ScrollToTop';
import PasswordChangeDrawer from '../PasswordChangeDrawer/PasswordChangeDrawer';
import usePasswordChange from '../PasswordChangeDrawer/usePasswordChange';
import UserCardHeader from './UserCardHeader';
import BackdropComponent from '../Backdrop/BackdropComponent';
import { useTitle } from '../../contexts/TitleContext';
import useNotifications from '../../pages/HomePrivado/hooks/useNotifications';
import NotificationsIcon from '@mui/icons-material/Notifications';
import logoSimp from '../../assets/logo-generico/Logo_SIMP_Claro.svg';
import logoSimpOscuro from '../../assets/logo-generico/Logo_SIMP_OSCURO.svg';
import NotificationsMenuBar from '../../pages/HomePrivado/components/Notifications/NotificationMenuBar';
import Breadcrumb from './Breadcrumb';
import PageTitle from './PageTitle';
import { RootState } from '../../redux/store';

type RoleProp = {
    user?: any;
};
const RenderManager = ({ user }: RoleProp) =>
    user ? <AdminLayout /> : <Navigate to="/login" replace />;

const PrivateLayout = () => {
    const { t } = useTranslation('layoutT');
    const { isMobile } = useIsMobile();
    const { role, isAdminBO, isSupervisorSIMP, isOperatorSIMP, isViewerBO, isPreventor } =
        useRoles();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { title } = useTitle();
    const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
    const [anchorNotification, setAnchorNotification] = useState<null | HTMLElement>(null);
    const [open, setOpen] = useState(false);
    const user: any = useAppSelector(selectCurrentUser);
    const [userLogout, { isLoading: loginOut }] = useLogoutMutation();

    const {
        notifications,
        setNotifications,
        newNotification,
        setNewNotification,
        openNotificationsMenu,
        notificationsUpdated,
        setOpenNotificationsMenu,
        ...props
    } = useNotifications();

    const userData = useAppSelector((state: RootState) => state.auth.user);
    const logoThemeDefault = isAdminBO || isPreventor ? logoSimpOscuro : logoSimp;

    useEffect(() => {
        //if (userData?.organizacion?.imagenes) {
        const favicon: any = document.querySelector('link[rel="icon"]');
        if (favicon && userData?.organizacion?.imagenes) {
            favicon.href =
                userData?.organizacion?.imagenes?.urlLogoFavicon ||
                'assets/logo-generico/generic-icon.svg?v=2'; //@TODO: Cambiar a la url de la imagen a una contsante
        }
        const { urlLogoHeaderWeb } = userData?.organizacion?.imagenes || {};
        const logoHeaderWeb: any = document.querySelector('img[alt="logo-appbar"]');

        if (logoHeaderWeb) {
            logoHeaderWeb.src = urlLogoHeaderWeb ?? logoThemeDefault;
        }
        //}
    }, [userData]);

    let displayTitleHeader: string;

    const simpTitle = t('simp');

    if (location.pathname === '/simp/home') {
        displayTitleHeader = simpTitle;
    } else if (location.pathname === '/backoffice/home') {
        displayTitleHeader = '';
    } else {
        displayTitleHeader = '';
    }

    const handleLogOut = async () => {
        const data = {
            username: user?.usuario
        };
        const response: any = await userLogout(data);
        if (response?.error) {
            handleErrors(response?.error);
        } else {
            dispatch(logout());
            navigate('/login');
        }
    };

    const { handleOpenDrawer, ...passwordChangeProps } = usePasswordChange({ handleLogOut });

    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElNav(event.currentTarget);
    };

    const handleOpenNotificationsMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorNotification(event.currentTarget);
        setOpenNotificationsMenu(true);
    };

    const handleCloseNotificationMenu = () => {
        setOpenNotificationsMenu(false);
        setAnchorNotification(null);
    };

    const handleDrawerToggle = () => {
        setOpen((open) => !open);
    };

    const logoutHandler = () => {
        /*  dispatch(
            showAlert({
                title: '¿Seguro que desea cerrar sesión?',
                cancelText: 'Cancelar',
                icon: 'info',
                confirmAction: handleLogOut
            })
        ); */
    };

    return (
        <Box
            className="layout-desktop-container container-main"
            display="block"
            flexDirection="column"
            justifyContent="space-between"
            minHeight="100vh"
        >
            <Box minHeight="100vh">
                <ScrollToTop />
                <CssBaseline />
                <AppBar
                    position="fixed"
                    open={open}
                    isMobile={isMobile}
                    id="app-bar"
                    sx={{ overflowX: 'auto', backgroundColor: 'white' }}
                >
                    <Toolbar sx={{ paddingRight: { xs: 0, sm: 2 } }}>
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{
                                mr: { sm: 1, md: 2 },
                                display: isMobile ? { sm: 'none' } : open ? 'none' : ''
                            }}
                        >
                            <MenuIcon color="primary" />
                        </IconButton>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ marginLeft: { xs: 0, sm: '3%' } }}
                            flexGrow={1}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                sx={{
                                    gap: { xs: '6px', sm: '12px' },
                                    margin: '0 3px',
                                    '& img': {
                                        height: { xs: '46px', sm: '50px', md: '56px' }
                                    },
                                    '& .MuiPaper-root': {
                                        boxShadow: 'none'
                                    },
                                    '& hr': {
                                        background: ' var(--primary)',
                                        width: '2px',
                                        height: '40px',
                                        marginTop: '10px'
                                    }
                                }}
                            >
                                <Box>
                                    <img
                                        alt="logo-appbar"
                                        src={
                                            isAdminBO || isPreventor
                                                ? logoThemeBlue
                                                : isOperatorSIMP || isSupervisorSIMP || isViewerBO
                                                ? logoThemeGreen
                                                : ''
                                        }
                                        style={{
                                            cursor: 'pointer',
                                            display: 'flex',
                                            padding: '8px',
                                            objectFit: 'cover'
                                        }}
                                        onClick={() => {
                                            open && handleDrawerToggle();
                                            navigate('/');
                                        }}
                                    />
                                </Box>
                                <Box sx={{ paddingLeft: '25px' }}>
                                    <div>
                                        <Breadcrumb />

                                        <PageTitle
                                            displayTitleHeader={displayTitleHeader}
                                            pageTitle={title}
                                        />
                                    </div>
                                </Box>

                                <Box></Box>

                                <Box></Box>
                            </Stack>
                            <Stack
                                sx={{
                                    margin: '0 3px',
                                    '& .MuiPaper-root': {
                                        boxShadow: 'none'
                                    },
                                    '& .MuiCard-root': {
                                        marginLeft: 0
                                    }
                                }}
                                direction="row"
                                spacing={3}
                                alignItems="center"
                            >
                                {!isViewerBO && !isMobile && !isPreventor && (
                                    <IconButton
                                        color="primary"
                                        onClick={handleOpenNotificationsMenu}
                                        sx={{
                                            '& svg': {
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '50%',
                                                border: (theme) =>
                                                    notifications?.data?.totalUnread > 0
                                                        ? '2px solid'
                                                        : `2px solid ${theme.palette.primary.main}`,
                                                borderColor: (theme) => theme.palette.primary.main
                                            },
                                            marginRight: '50px !important'
                                        }}
                                    >
                                        <Badge
                                            className={`animation-badge${
                                                notificationsUpdated ? '-scaled' : ''
                                            } badge-primary`}
                                            badgeContent={notifications?.data?.totalUnread}
                                            sx={{
                                                '& .MuiBadge-anchorOriginTopRight': {
                                                    fontSize: '15px',
                                                    top: '5px',
                                                    right: '5px'
                                                },
                                                //backgroundColor: 'blue',//(theme: any) => theme.palette.primary.light,
                                                borderRadius: '50%'
                                            }}
                                            color="error"
                                        >
                                            <NotificationsIcon
                                                sx={{
                                                    color: (theme: any) =>
                                                        theme.palette.primary.main,
                                                    width: 24,
                                                    height: 24
                                                }}
                                            />
                                        </Badge>
                                    </IconButton>
                                )}
                                <UserCardHeader
                                    role={role}
                                    user={user}
                                    handleOpenNavMenu={handleOpenNavMenu}
                                    isMobile={isMobile}
                                />
                            </Stack>
                        </Stack>
                    </Toolbar>
                </AppBar>
                <SideMenu open={open} isMobile={isMobile} handleDrawerToggle={handleDrawerToggle} />
                <Box
                    component="main"
                    sx={{
                        minHeight: '100vh'
                    }}
                >
                    <Box className="container">
                        <Routes>
                            <Route path="*" element={<RenderManager user={user} />} />
                            <Route path="*" element={<RenderManager />} />
                        </Routes>
                    </Box>
                </Box>
            </Box>

            <NotificationsMenuBar
                isMenu
                openMenu={openNotificationsMenu}
                handleClose={handleCloseNotificationMenu}
                anchorEl={anchorNotification}
                notifications={notifications}
                {...props}
            />

            <PasswordChangeDrawer {...passwordChangeProps} />
            <BackdropComponent loading={loginOut} />
        </Box>
    );
};

export default PrivateLayout;
